// DsvPortfolioTab.js — Tab 2: DSV Portfolio allocation table + aggregate charts.
// Pulls market configs from Tab 1 (MarketsTab), runs the DSV engine, displays results.

import { useState, useMemo, useEffect, useCallback } from 'react';
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip,
} from 'recharts';
import { useSession, useSessionDispatch } from './SessionContext';
import { DEFAULT_STRATEGIES, simulate } from '../../lib/dsv-engine';
import { getNavData } from '../../lib/nav-data-loader';
import COLORS from '../../lib/colors';

// ── Strategy Colors (23 distinct — matches DsvSimulator) ──

const STRATEGY_COLORS = [
  '#2563EB', '#DC2626', '#059669', '#D97706', '#7C3AED',
  '#DB2777', '#0891B2', '#65A30D', '#EA580C', '#4F46E5',
  '#BE185D', '#0D9488', '#CA8A04', '#9333EA', '#E11D48',
  '#0284C7', '#16A34A', '#C2410C', '#6D28D9', '#047857',
  '#B91C1C', '#1D4ED8', '#15803D',
];

// ── Formatting helpers ──

const fmtPct = (v, d = 2) => (v * 100).toFixed(d) + '%';

// ── Name mapping: DSV strategy name → Tab 1 market name ──
// All DSV strategies now have a matching Tab 1 market preset (same name).
const DSV_TO_MARKET = Object.fromEntries(
  DEFAULT_STRATEGIES.map(s => [s.name, s.name])
);

// ── Inline UI components (same patterns as MarketsTab) ──

const Card = ({ title, children }) => (
  <div style={{ background: COLORS.surface, border: `1px solid ${COLORS.borderLight}`, borderRadius: 4, padding: 16, marginBottom: 12 }}>
    {title && <div style={{ fontFamily: "'Fragment Mono', monospace", fontSize: 11, letterSpacing: '0.08em', textTransform: 'uppercase', color: COLORS.textSecondary, marginBottom: 12 }}>{title}</div>}
    {children}
  </div>
);

const MetricRow = ({ label, value, color }) => (
  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '4px 0' }}>
    <span style={{ fontFamily: "'Fragment Mono', monospace", fontSize: 11, color: COLORS.textSecondary }}>{label}</span>
    <span style={{ fontFamily: "'Fragment Mono', monospace", fontSize: 13, fontWeight: 600, color: color || COLORS.ink }}>{value}</span>
  </div>
);

const Toggle = ({ label, value, onChange }) => (
  <div
    onClick={() => onChange(!value)}
    style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', userSelect: 'none' }}
  >
    <div style={{
      width: 36, height: 20, borderRadius: 10,
      background: value ? COLORS.accent : COLORS.borderDark,
      border: `1px solid ${value ? COLORS.accent : COLORS.borderDark}`,
      position: 'relative', transition: 'all 0.2s', flexShrink: 0,
    }}>
      <div style={{
        width: 16, height: 16, borderRadius: 8,
        background: value ? COLORS.bg : COLORS.textTertiary,
        position: 'absolute', top: 1, left: value ? 18 : 1,
        transition: 'left 0.2s',
      }} />
    </div>
    {label && <span style={{ fontFamily: "'Fragment Mono', monospace", fontSize: 11, color: COLORS.textSecondary }}>{label}</span>}
  </div>
);

const ChartTooltip = ({ active, payload, labelFormatter }) => {
  if (!active || !payload?.length) return null;
  return (
    <div style={{ background: COLORS.ink, color: COLORS.bg, padding: '8px 12px', borderRadius: 2, fontFamily: "'Fragment Mono', monospace", fontSize: 11, lineHeight: 1.6, boxShadow: '0 4px 12px rgba(0,0,0,0.3)' }}>
      {labelFormatter && <div style={{ marginBottom: 2 }}>{labelFormatter(payload[0]?.payload)}</div>}
      {payload.map((p, i) => (
        <div key={i} style={{ color: p.color }}>{p.name}: {typeof p.value === 'number' ? fmtPct(p.value) : p.value}</div>
      ))}
    </div>
  );
};

// ── Number input (small, inline — matches DsvSimulator pattern) ──

const NumberInput = ({ value, onChange, min, max, step, suffix, width, decimals: decimalsProp }) => {
  const s = step || 1;
  const decimals = decimalsProp != null ? decimalsProp : (s < 0.01 ? 3 : s < 0.1 ? 2 : s < 1 ? 1 : 0);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const startEdit = () => { setDraft(typeof value === 'number' ? value.toFixed(decimals) : String(value)); setEditing(true); };
  const commitEdit = () => {
    setEditing(false);
    const v = parseFloat(draft);
    if (!isNaN(v)) onChange(Math.min(Math.max(v, min), max));
  };
  return (
    <div style={{ display: 'inline-flex', alignItems: 'baseline', gap: 2 }}>
      {editing ? (
        <input type="text" autoFocus value={draft} onChange={(e) => setDraft(e.target.value)}
          onBlur={commitEdit} onKeyDown={(e) => { if (e.key === 'Enter') commitEdit(); if (e.key === 'Escape') setEditing(false); }}
          style={{ fontFamily: "'Fragment Mono', monospace", fontSize: 12, fontWeight: 600, color: COLORS.ink, background: COLORS.bg, border: `1px solid ${COLORS.accent}`, borderRadius: 2, padding: '2px 6px', width: width || 52, textAlign: 'right', outline: 'none' }} />
      ) : (
        <span onClick={startEdit} style={{ fontFamily: "'Fragment Mono', monospace", fontSize: 12, fontWeight: 600, color: COLORS.ink, cursor: 'text', borderBottom: `1px dashed ${COLORS.champagne}80`, paddingBottom: 1 }}>
          {typeof value === 'number' ? value.toFixed(decimals) : value}
        </span>
      )}
      {suffix && <span style={{ fontFamily: "'Fragment Mono', monospace", fontSize: 11, color: COLORS.textSecondary }}>{suffix}</span>}
    </div>
  );
};

// ── Main component ──

export default function DsvPortfolioTab() {
  const { state } = useSession();
  const dispatch = useSessionDispatch();
  const [showPerStrategy, setShowPerStrategy] = useState(false);
  const [dateRangeMode, setDateRangeMode] = useState('custom90'); // 'latest90' | 'custom90' | 'full'
  const [customStartDate, setCustomStartDate] = useState('2025-11-28');

  // ── Seed allocations on first render ──
  useEffect(() => {
    if (Object.keys(state.dsv.allocations).length === 0) {
      for (const strat of DEFAULT_STRATEGIES) {
        dispatch({
          type: 'UPDATE_DSV_ALLOCATION',
          name: strat.name,
          patch: { enabled: strat.enabled, allocation: strat.allocation, overrideTab1: false },
        });
      }
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Sim params helpers ──
  const simParams = state.dsv.simParams;

  const updateSimParam = useCallback((key, value) => {
    dispatch({ type: 'UPDATE_DSV', patch: { simParams: { [key]: value } } });
  }, [dispatch]);

  const updateAllocation = useCallback((name, patch) => {
    dispatch({ type: 'UPDATE_DSV_ALLOCATION', name, patch });
  }, [dispatch]);

  // ── Compute total allocation ──
  const totalAllocation = useMemo(() => {
    return DEFAULT_STRATEGIES.reduce((sum, s) => {
      const alloc = state.dsv.allocations[s.name];
      if (!alloc || !alloc.enabled) return sum;
      return sum + (alloc.allocation || 0);
    }, 0);
  }, [state.dsv.allocations]);

  // ── Build strategies array + NAV data, run simulate() ──
  const dsvResult = useMemo(() => {
    const strategies = DEFAULT_STRATEGIES.map(defaultStrat => {
      const alloc = state.dsv.allocations[defaultStrat.name];
      if (!alloc) return { ...defaultStrat, enabled: false };

      const base = { ...defaultStrat, enabled: alloc.enabled, allocation: alloc.allocation };

      // If linked to Tab 1 and market exists, override params from Tab 1
      if (!alloc.overrideTab1 && defaultStrat.minCoverage > 0) {
        const marketName = DSV_TO_MARKET[defaultStrat.name];
        const market = marketName ? state.markets[marketName] : null;
        if (market) {
          base.minCoverage = market.params.minCoverage;
          base.recoveryDuration = market.params.recoveryDuration;
          base.seniorFee = market.params.stFee;
          base.juniorFee = market.params.jtFee;
          base.yieldShareFee = market.params.ysFee;
          base.finalizedLossThreshold = market.liquidation.finalizedLossThreshold;
          base.selfLiquidationBonus = market.liquidation.selfLiquidationBonus;
        }
      }

      return base;
    });

    // Build navData from nav-data-loader, then apply date range filter
    const navData = {};
    for (const strat of strategies) {
      if (!strat.enabled) continue;
      const nd = getNavData(strat.name);
      if (nd) {
        navData[strat.name] = { dates: nd.dates, navs: nd.navs };
      }
    }

    const raw = simulate({
      strategies,
      navData: Object.keys(navData).length > 0 ? navData : null,
      simParams: {
        reversionDays: simParams.reversionDays,
        liquidationBonusMode: simParams.liquidationBonusMode,
        liquidationRedemptionRate: simParams.liquidationRedemptionRate,
      },
    });

    // Post-filter: trim simulation output to the requested window
    // This operates on common dates (after intersection), so we always get
    // exactly 90 days regardless of per-strategy data gaps.
    const trimResult = (result, offset, count) => {
      const trimmedDates = result.dates.slice(offset, offset + count);
      const days = (Date.parse(trimmedDates[trimmedDates.length - 1]) - Date.parse(trimmedDates[0])) / 86400000;
      const trimmedStrategies = {};
      for (const [name, s] of Object.entries(result.strategies)) {
        const stBase = s.seniorCumReturn[offset];
        const uBase = s.underlyingCumReturn[offset];
        trimmedStrategies[name] = {
          ...s,
          seniorCumReturn: s.seniorCumReturn.slice(offset, offset + count).map(v => (1 + v) / (1 + stBase) - 1),
          underlyingCumReturn: s.underlyingCumReturn.slice(offset, offset + count).map(v => (1 + v) / (1 + uBase) - 1),
          jtEffectiveNav: s.jtEffectiveNav.slice(offset, offset + count),
          marketStateHistory: s.marketStateHistory?.slice(offset, offset + count),
        };
        const stTotal = trimmedStrategies[name].seniorCumReturn[trimmedDates.length - 1];
        trimmedStrategies[name].summary = {
          ...s.summary,
          totalReturn: stTotal,
          days,
          annualizedReturn: days > 0 ? Math.pow(1 + stTotal, 365 / days) - 1 : 0,
        };
      }
      const aggBase = result.aggregate.dsvCumReturn[offset];
      const trimmedAggCum = result.aggregate.dsvCumReturn.slice(offset, offset + count).map(v => (1 + v) / (1 + aggBase) - 1);
      const totalReturn = trimmedAggCum[trimmedDates.length - 1];
      return {
        dates: trimmedDates,
        strategies: trimmedStrategies,
        aggregate: {
          dsvCumReturn: trimmedAggCum,
          summary: { totalReturn, annualizedReturn: days > 0 ? Math.pow(1 + totalReturn, 365 / days) - 1 : 0, days },
        },
      };
    };

    if (dateRangeMode === 'latest90' && raw.dates.length > 91) {
      return trimResult(raw, raw.dates.length - 91, 91);
    }

    if (dateRangeMode === 'custom90' && customStartDate && raw.dates.length > 0) {
      const startIdx = raw.dates.findIndex(d => d >= customStartDate);
      if (startIdx >= 0) {
        return trimResult(raw, startIdx, Math.min(91, raw.dates.length - startIdx));
      }
    }

    return raw;
  }, [state.markets, state.dsv.allocations, simParams, dateRangeMode, customStartDate]);

  // ── Chart data: Aggregate cumulative return ──
  const cumReturnData = useMemo(() => {
    if (!dsvResult || !dsvResult.dates || dsvResult.dates.length === 0) return [];
    const enabledNames = Object.keys(dsvResult.strategies);
    return dsvResult.dates.map((date, i) => {
      const point = { date, aggregate: dsvResult.aggregate.dsvCumReturn[i] };
      if (showPerStrategy) {
        for (const name of enabledNames) {
          point[name] = dsvResult.strategies[name].seniorCumReturn[i];
        }
      }
      return point;
    });
  }, [dsvResult, showPerStrategy]);

  // ── Chart data: Trailing 7-day APY ──
  const trailingApyData = useMemo(() => {
    if (!dsvResult || dsvResult.dates.length < 8) return [];
    const cum = dsvResult.aggregate.dsvCumReturn;
    return dsvResult.dates.map((date, i) => {
      if (i < 7) return { date, apy: null };
      const weekReturn = (1 + cum[i]) / (1 + cum[i - 7]) - 1;
      const annualized = Math.pow(1 + weekReturn, 365 / 7) - 1;
      return { date, apy: annualized };
    }).filter(d => d.apy !== null);
  }, [dsvResult]);

  // ── Enabled strategy names + color map ──
  const enabledNames = useMemo(() => Object.keys(dsvResult.strategies), [dsvResult]);
  const strategyColorMap = useMemo(() => {
    const map = {};
    enabledNames.forEach((name, i) => { map[name] = STRATEGY_COLORS[i % STRATEGY_COLORS.length]; });
    return map;
  }, [enabledNames]);

  // ── Split strategies into Dawn Markets vs Reserve ──
  const dawnStrategies = DEFAULT_STRATEGIES.filter(s => s.minCoverage > 0);
  const reserveStrategies = DEFAULT_STRATEGIES.filter(s => s.minCoverage <= 0);

  // ── Tab 1 link status for a strategy ──
  const getTab1Status = (stratName) => {
    const marketName = DSV_TO_MARKET[stratName];
    if (!marketName) return 'none'; // no Tab 1 config
    const market = state.markets[marketName];
    if (!market) return 'incomplete';
    return market.status === 'complete' ? 'complete' : 'incomplete';
  };

  // ── Strategy row renderer ──
  const StrategyRow = ({ strat }) => {
    const alloc = state.dsv.allocations[strat.name] || { enabled: false, allocation: 0, overrideTab1: false };
    const tab1Status = strat.minCoverage > 0 ? getTab1Status(strat.name) : 'none';
    const marketName = DSV_TO_MARKET[strat.name];

    return (
      <tr style={{ borderBottom: `1px solid ${COLORS.borderLight}` }}>
        {/* Enable checkbox */}
        <td style={{ padding: '8px 6px', textAlign: 'center' }}>
          <input
            type="checkbox"
            checked={alloc.enabled}
            onChange={(e) => updateAllocation(strat.name, { enabled: e.target.checked })}
            style={{ cursor: 'pointer', accentColor: COLORS.accent }}
          />
        </td>
        {/* Strategy name */}
        <td style={{ padding: '8px 6px', fontFamily: "'Fragment Mono', monospace", fontSize: 12, color: alloc.enabled ? COLORS.ink : COLORS.textTertiary }}>
          {strat.name}
          {enabledNames.includes(strat.name) && (
            <span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: '50%', background: strategyColorMap[strat.name], marginLeft: 6, verticalAlign: 'middle' }} />
          )}
        </td>
        {/* Allocation % */}
        <td style={{ padding: '8px 6px', textAlign: 'right' }}>
          <NumberInput
            value={parseFloat((alloc.allocation * 100).toFixed(2))}
            onChange={(v) => updateAllocation(strat.name, { allocation: v / 100 })}
            min={0}
            max={100}
            step={0.5}
            suffix="%"
            width={52}
          />
        </td>
        {/* Tab 1 link indicator */}
        <td style={{ padding: '8px 6px', textAlign: 'center', fontFamily: "'Fragment Mono', monospace", fontSize: 13 }}>
          {tab1Status === 'complete' && <span title={`Linked to ${marketName}`} style={{ color: COLORS.positive }}>{'\u25CF'}</span>}
          {tab1Status === 'incomplete' && <span title={`${marketName} incomplete`} style={{ color: COLORS.caution }}>{'\u25CB'}</span>}
          {tab1Status === 'none' && <span style={{ color: COLORS.textTertiary }}>{'\u2014'}</span>}
        </td>
        {/* Override toggle */}
        <td style={{ padding: '8px 6px', textAlign: 'center' }}>
          {strat.minCoverage > 0 && marketName ? (
            <Toggle value={alloc.overrideTab1} onChange={(v) => updateAllocation(strat.name, { overrideTab1: v })} />
          ) : (
            <span style={{ color: COLORS.textTertiary }}>{'\u2014'}</span>
          )}
        </td>
      </tr>
    );
  };

  // ── Table header styles ──
  const thStyle = {
    fontFamily: "'Fragment Mono', monospace", fontSize: 10, letterSpacing: '0.08em',
    textTransform: 'uppercase', color: COLORS.textTertiary, padding: '8px 6px',
    borderBottom: `2px solid ${COLORS.borderLight}`, textAlign: 'left', fontWeight: 400,
  };

  // ── Render ──
  return (
    <div style={{ padding: '20px 24px' }}>
      <div style={{ display: 'grid', gridTemplateColumns: '420px 1fr', gap: 20, alignItems: 'start' }}>
        {/* ── Left Panel: Settings ── */}
        <div>
          {/* Strategy Table */}
          <Card title="Strategy Allocations">
            {/* Dawn Markets section */}
            <div style={{ fontFamily: "'Fragment Mono', monospace", fontSize: 10, letterSpacing: '0.08em', textTransform: 'uppercase', color: COLORS.accent, marginBottom: 8, marginTop: 4 }}>
              Dawn Markets (with JT coverage)
            </div>
            <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: 16 }}>
              <thead>
                <tr>
                  <th style={{ ...thStyle, width: 32, textAlign: 'center' }}>On</th>
                  <th style={thStyle}>Strategy</th>
                  <th style={{ ...thStyle, width: 80, textAlign: 'right' }}>Alloc</th>
                  <th style={{ ...thStyle, width: 40, textAlign: 'center' }}>Tab 1</th>
                  <th style={{ ...thStyle, width: 56, textAlign: 'center' }}>Override</th>
                </tr>
              </thead>
              <tbody>
                {dawnStrategies.map(s => <StrategyRow key={s.name} strat={s} />)}
              </tbody>
            </table>

            {/* Reserve Strategies section */}
            <div style={{ fontFamily: "'Fragment Mono', monospace", fontSize: 10, letterSpacing: '0.08em', textTransform: 'uppercase', color: COLORS.textSecondary, marginBottom: 8 }}>
              Reserve Strategies (no JT)
            </div>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  <th style={{ ...thStyle, width: 32, textAlign: 'center' }}>On</th>
                  <th style={thStyle}>Strategy</th>
                  <th style={{ ...thStyle, width: 80, textAlign: 'right' }}>Alloc</th>
                  <th style={{ ...thStyle, width: 40, textAlign: 'center' }}>Tab 1</th>
                  <th style={{ ...thStyle, width: 56, textAlign: 'center' }}>Override</th>
                </tr>
              </thead>
              <tbody>
                {reserveStrategies.map(s => <StrategyRow key={s.name} strat={s} />)}
              </tbody>
            </table>

            {/* Total allocation indicator */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 12 }}>
              <span style={{
                fontFamily: "'Fragment Mono', monospace", fontSize: 12, fontWeight: 600,
                color: Math.abs(totalAllocation - 1) < 0.005 ? COLORS.positive : COLORS.negative,
              }}>
                Total: {fmtPct(totalAllocation, 1)}
              </span>
              {Math.abs(totalAllocation - 1) >= 0.005 && (
                <span style={{ fontFamily: "'Fragment Mono', monospace", fontSize: 10, color: COLORS.negative }}>
                  (should be ~100%)
                </span>
              )}
            </div>
          </Card>

          {/* Sim Params */}
          <Card title="Simulation Parameters">
            <div style={{ marginBottom: 10 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                <span style={{ fontFamily: "'Fragment Mono', monospace", fontSize: 10, letterSpacing: '0.08em', textTransform: 'uppercase', color: COLORS.textSecondary }}>New Junior Arrival Days</span>
                <NumberInput value={simParams.reversionDays} onChange={(v) => updateSimParam('reversionDays', v)} min={1} max={90} step={1} width={48} />
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                <span style={{ fontFamily: "'Fragment Mono', monospace", fontSize: 10, letterSpacing: '0.08em', textTransform: 'uppercase', color: COLORS.textSecondary }}>Liq Bonus Mode</span>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <Toggle
                    value={simParams.liquidationBonusMode === 'optimistic'}
                    onChange={(v) => updateSimParam('liquidationBonusMode', v ? 'optimistic' : 'pessimistic')}
                  />
                  <span style={{ fontFamily: "'Fragment Mono', monospace", fontSize: 11, color: COLORS.ink }}>
                    {simParams.liquidationBonusMode}
                  </span>
                </div>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontFamily: "'Fragment Mono', monospace", fontSize: 10, letterSpacing: '0.08em', textTransform: 'uppercase', color: COLORS.textSecondary }}>Liq Redemption Rate</span>
                <NumberInput
                  value={parseFloat((simParams.liquidationRedemptionRate * 100).toFixed(1))}
                  onChange={(v) => updateSimParam('liquidationRedemptionRate', v / 100)}
                  min={0} max={100} step={1} suffix="%" width={48}
                />
              </div>
            </div>
          </Card>

          {/* Completion toggle */}
          <button
            onClick={() => dispatch({ type: 'SET_DSV_STATUS', status: state.dsv.status === 'complete' ? 'incomplete' : 'complete' })}
            style={{
              width: '100%', padding: '10px 0', fontFamily: "'Fragment Mono', monospace", fontSize: 12,
              letterSpacing: '0.05em', textTransform: 'uppercase', cursor: 'pointer', borderRadius: 4,
              background: state.dsv.status === 'complete' ? COLORS.positiveBg : 'transparent',
              color: state.dsv.status === 'complete' ? COLORS.positive : COLORS.textSecondary,
              border: `1px solid ${state.dsv.status === 'complete' ? COLORS.positiveBorder : COLORS.borderLight}`,
            }}
          >
            {state.dsv.status === 'complete' ? 'Mark Incomplete' : 'Mark Complete'}
          </button>
        </div>

        {/* ── Right Panel: Charts ── */}
        <div>
          {/* Date range selector */}
          <Card title="Date Range">
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '4px', background: COLORS.surfaceAlt, border: `1px solid ${COLORS.borderLight}`, borderRadius: 4 }}>
                {[
                  { key: 'full', label: 'Max compatible' },
                  { key: 'latest90', label: 'Latest 90d' },
                  { key: 'custom90', label: '90d from' },
                ].map(({ key, label }) => (
                  <button key={key} onClick={() => setDateRangeMode(key)} style={{
                    fontFamily: "'Fragment Mono', monospace", fontSize: 11, padding: '4px 10px',
                    background: dateRangeMode === key ? COLORS.ink : 'transparent',
                    color: dateRangeMode === key ? COLORS.bg : COLORS.textSecondary,
                    border: 'none', borderRadius: 3, cursor: 'pointer', whiteSpace: 'nowrap',
                  }}>
                    {label}
                  </button>
                ))}
                {dateRangeMode === 'custom90' && (
                  <input
                    type="date"
                    value={customStartDate}
                    onChange={(e) => setCustomStartDate(e.target.value)}
                    style={{
                      fontFamily: "'Fragment Mono', monospace", fontSize: 11, padding: '3px 6px',
                      background: COLORS.bg, color: COLORS.ink, border: `1px solid ${COLORS.borderLight}`,
                      borderRadius: 3, outline: 'none', marginLeft: 2,
                    }}
                  />
                )}
              </div>
              {dsvResult.dates.length > 0 && (
                <span style={{ fontFamily: "'Fragment Mono', monospace", fontSize: 11, color: COLORS.textTertiary }}>
                  {dsvResult.dates[0]} → {dsvResult.dates[dsvResult.dates.length - 1]} ({Math.round((Date.parse(dsvResult.dates[dsvResult.dates.length - 1]) - Date.parse(dsvResult.dates[0])) / 86400000)}d)
                </span>
              )}
            </div>
          </Card>

          {/* Portfolio Summary */}
          {dsvResult.aggregate.summary.days > 0 && (
            <Card title="Portfolio Summary">
              <MetricRow label="Total Return" value={fmtPct(dsvResult.aggregate.summary.totalReturn)} color={dsvResult.aggregate.summary.totalReturn >= 0 ? COLORS.positive : COLORS.negative} />
              <MetricRow label="Annualized Return" value={fmtPct(dsvResult.aggregate.summary.annualizedReturn)} color={COLORS.accent} />
              <MetricRow label="Backtest Period" value={`${Math.round(dsvResult.aggregate.summary.days)} days`} />
              <MetricRow label="Enabled Strategies" value={enabledNames.length} />
              {Math.abs(totalAllocation - 1) >= 0.005 && (
                <div style={{ marginTop: 10, padding: '6px 10px', background: COLORS.cautionBg, border: `1px solid ${COLORS.cautionBorder}`, borderRadius: 2, fontFamily: "'Fragment Mono', monospace", fontSize: 11, color: COLORS.caution }}>
                  {fmtPct(totalAllocation, 1)} of capital allocated — {totalAllocation < 1 ? `${fmtPct(1 - totalAllocation, 1)} idle` : `${fmtPct(totalAllocation - 1, 1)} over-allocated`}
                </div>
              )}

              {/* Per-strategy summary table */}
              {enabledNames.length > 0 && (
                <div style={{ marginTop: 16 }}>
                  <div style={{ fontFamily: "'Fragment Mono', monospace", fontSize: 10, letterSpacing: '0.08em', textTransform: 'uppercase', color: COLORS.textTertiary, marginBottom: 8 }}>Per-Strategy Breakdown</div>
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr>
                        <th style={thStyle}>Strategy</th>
                        <th style={{ ...thStyle, textAlign: 'right' }}>Weight</th>
                        <th style={{ ...thStyle, textAlign: 'right' }}>Total</th>
                        <th style={{ ...thStyle, textAlign: 'right' }}>Ann.</th>
                      </tr>
                    </thead>
                    <tbody>
                      {enabledNames.map(name => {
                        const s = dsvResult.strategies[name]?.summary;
                        if (!s) return null;
                        return (
                          <tr key={name} style={{ borderBottom: `1px solid ${COLORS.borderLight}` }}>
                            <td style={{ padding: '6px', fontFamily: "'Fragment Mono', monospace", fontSize: 11, color: COLORS.ink }}>
                              <span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: '50%', background: strategyColorMap[name], marginRight: 6, verticalAlign: 'middle' }} />
                              {name}
                            </td>
                            <td style={{ padding: '6px', fontFamily: "'Fragment Mono', monospace", fontSize: 11, color: COLORS.textSecondary, textAlign: 'right' }}>{fmtPct(s.weight, 1)}</td>
                            <td style={{ padding: '6px', fontFamily: "'Fragment Mono', monospace", fontSize: 11, color: s.totalReturn >= 0 ? COLORS.positive : COLORS.negative, textAlign: 'right' }}>{fmtPct(s.totalReturn)}</td>
                            <td style={{ padding: '6px', fontFamily: "'Fragment Mono', monospace", fontSize: 11, color: COLORS.ink, textAlign: 'right' }}>{fmtPct(s.annualizedReturn)}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </Card>
          )}

          {/* Aggregate DSV Cumulative Return */}
          <Card title="Aggregate DSV Cumulative Return">
            <div style={{ marginBottom: 8 }}>
              <Toggle label="Show per-strategy lines" value={showPerStrategy} onChange={setShowPerStrategy} />
            </div>
            {cumReturnData.length > 0 ? (
              <ResponsiveContainer width="100%" height={400}>
                <LineChart data={cumReturnData} margin={{ top: 10, right: 30, left: 10, bottom: 10 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke={COLORS.borderLight} />
                  <XAxis
                    dataKey="date"
                    tick={{ fontSize: 10, fill: COLORS.textTertiary, fontFamily: "'Fragment Mono', monospace" }}
                    tickFormatter={(d) => d.slice(5)}
                    interval={Math.max(1, Math.floor(cumReturnData.length / 12))}
                  />
                  <YAxis
                    tickFormatter={(v) => (v * 100).toFixed(1) + '%'}
                    tick={{ fontSize: 10, fill: COLORS.textTertiary, fontFamily: "'Fragment Mono', monospace" }}
                    width={60}
                  />
                  <Tooltip content={<ChartTooltip labelFormatter={(d) => d.date} />} />
                  {showPerStrategy && enabledNames.map((name) => (
                    <Line
                      key={name}
                      type="monotone"
                      dataKey={name}
                      stroke={strategyColorMap[name]}
                      strokeWidth={1.2}
                      dot={false}
                      name={name}
                    />
                  ))}
                  <Line
                    type="monotone"
                    dataKey="aggregate"
                    stroke={COLORS.accent}
                    strokeWidth={2.5}
                    dot={false}
                    name="DSV Aggregate"
                  />
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <div style={{ textAlign: 'center', padding: 40, color: COLORS.textTertiary, fontFamily: "'Fragment Mono', monospace", fontSize: 12 }}>
                No enabled strategies to chart.
              </div>
            )}
          </Card>

          {/* Trailing 7-Day APY */}
          <Card title="Trailing 7-Day APY">
            {trailingApyData.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={trailingApyData} margin={{ top: 10, right: 30, left: 10, bottom: 10 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke={COLORS.borderLight} />
                  <XAxis
                    dataKey="date"
                    tick={{ fontSize: 10, fill: COLORS.textTertiary, fontFamily: "'Fragment Mono', monospace" }}
                    tickFormatter={(d) => d.slice(5)}
                    interval={Math.max(1, Math.floor(trailingApyData.length / 12))}
                  />
                  <YAxis
                    tickFormatter={(v) => (v * 100).toFixed(1) + '%'}
                    tick={{ fontSize: 10, fill: COLORS.textTertiary, fontFamily: "'Fragment Mono', monospace" }}
                    width={60}
                  />
                  <Tooltip content={<ChartTooltip labelFormatter={(d) => d.date} />} />
                  <Line
                    type="monotone"
                    dataKey="apy"
                    stroke={COLORS.accent}
                    strokeWidth={1.5}
                    dot={false}
                    name="Trailing 7d APY"
                  />
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <div style={{ textAlign: 'center', padding: 40, color: COLORS.textTertiary, fontFamily: "'Fragment Mono', monospace", fontSize: 12 }}>
                Not enough data for trailing APY (need 8+ days).
              </div>
            )}
          </Card>
        </div>
      </div>
    </div>
  );
}
